import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import "./lib/i18n";
import { AuthProvider } from "./contexts/AuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { ScrollToTop } from "./components/ScrollToTop";
import { PublicLayout } from "./components/layout/PublicLayout";

import HomePage from "./pages/public/HomePage";
import SearchPage from "./pages/public/SearchPage";
import EventPage from "./pages/public/EventPage";
import LoginPage from "./pages/public/LoginPage";
import RegisterPage from "./pages/public/RegisterPage";
import ForgotPasswordPage from "./pages/public/ForgotPasswordPage";
import BookingPage from "./pages/public/BookingPage";
import ThankYouPage from "./pages/public/ThankYouPage";
import {
    ContactPage,
    AboutPage,
    TermsPage,
    PrivacyPage,
    RefundPolicyPage,
} from "./pages/public/StaticPages";

import ProfilePage from "./pages/user/ProfilePage";
import BookingsPage from "./pages/user/BookingsPage";
import BecomeOrganizerPage from "./pages/user/BecomeOrganizerPage";

import EventFormPage from "./pages/organizer/EventFormPage";
import MyListingsPage from "./pages/organizer/MyListingsPage";
import ManageEventLayout from "./pages/organizer/ManageEventLayout";
import { EventDashboardPage, SoldTicketsPage } from "./pages/organizer/ManageEventPages";
import { EventStaffPage } from "./pages/organizer/EventStaffPage";
import { CheckInPage } from "./pages/organizer/CheckInPage";
import WalletPage from "./pages/organizer/WalletPage";
import { StaffEventsPage } from "./pages/organizer/StaffEventsPage";

import AdminLayout from "./pages/admin/AdminLayout";
import AdminReferencePage from "./pages/admin/AdminReferencePage";
import { AdminVenuesPage, AdminArtistsPage } from "./pages/admin/AdminVenuesArtistsPage";
import {
    AdminBookingsPage,
    AdminEventsPage,
    AdminPayoutsPage,
    AdminSystemPage,
    AdminTopEventsPage,
} from "./pages/admin/AdminManagementPages";
import { AdminDashboardPage } from "./pages/admin/AdminOtherPages";

function App() {
    return (
        <div className="App">
            <AuthProvider>
                <BrowserRouter>
                    <ScrollToTop />
                    <Toaster position="top-right" richColors closeButton />
                    <Routes>
                        {/* Public routes */}
                        <Route element={<PublicLayout />}>
                            <Route index element={<HomePage />} />
                            <Route path="/search" element={<SearchPage />} />
                            <Route path="/events/:id" element={<EventPage />} />
                            <Route path="/events/:id/book" element={<BookingPage />} />
                            <Route path="/thank-you" element={<ThankYouPage />} />
                            <Route path="/login" element={<LoginPage />} />
                            <Route path="/register" element={<RegisterPage />} />
                            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                            <Route path="/contact" element={<ContactPage />} />
                            <Route path="/about" element={<AboutPage />} />
                            <Route path="/terms" element={<TermsPage />} />
                            <Route path="/privacy" element={<PrivacyPage />} />
                            <Route path="/refund-policy" element={<RefundPolicyPage />} />

                            {/* User */}
                            <Route
                                path="/profile"
                                element={
                                    <ProtectedRoute>
                                        <ProfilePage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/bookings"
                                element={
                                    <ProtectedRoute>
                                        <BookingsPage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/become-organizer"
                                element={
                                    <ProtectedRoute>
                                        <BecomeOrganizerPage />
                                    </ProtectedRoute>
                                }
                            />

                            {/* Staff check-in */}
                            <Route
                                path="/staff"
                                element={
                                    <ProtectedRoute requireStaff>
                                        <StaffEventsPage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/staff/events/:id/checkin"
                                element={
                                    <ProtectedRoute requireStaff>
                                        <CheckInPage />
                                    </ProtectedRoute>
                                }
                            />

                            {/* Organizer */}
                            <Route
                                path="/organizer/listings"
                                element={
                                    <ProtectedRoute requireOrganizer>
                                        <MyListingsPage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/organizer/create-event"
                                element={
                                    <ProtectedRoute requireOrganizer>
                                        <EventFormPage mode="create" />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/organizer/wallet"
                                element={
                                    <ProtectedRoute requireOrganizer>
                                        <WalletPage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/organizer/events/:id/manage"
                                element={
                                    <ProtectedRoute>
                                        <ManageEventLayout />
                                    </ProtectedRoute>
                                }
                            >
                                <Route index element={<Navigate to="dashboard" replace />} />
                                <Route path="dashboard" element={<EventDashboardPage />} />
                                <Route path="edit" element={<EventFormPage mode="edit" />} />
                                <Route path="sold" element={<SoldTicketsPage />} />
                                <Route path="staff" element={<EventStaffPage />} />
                                <Route path="checkin" element={<CheckInPage />} />
                            </Route>
                        </Route>

                        {/* Admin */}
                        <Route
                            path="/admin"
                            element={
                                <ProtectedRoute requireAdmin>
                                    <AdminLayout />
                                </ProtectedRoute>
                            }
                        >
                            <Route index element={<AdminDashboardPage />} />
                            <Route path="reference" element={<AdminReferencePage />} />
                            <Route path="venues" element={<AdminVenuesPage />} />
                            <Route path="artists" element={<AdminArtistsPage />} />
                            <Route path="bookings" element={<AdminBookingsPage />} />
                            <Route path="events" element={<AdminEventsPage />} />
                            <Route path="payouts" element={<AdminPayoutsPage />} />
                            <Route path="system" element={<AdminSystemPage />} />
                            <Route path="top-events" element={<AdminTopEventsPage />} />
                        </Route>

                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </BrowserRouter>
            </AuthProvider>
        </div>
    );
}

export default App;
